import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateReferralCodePayload,
  CreateReferralRewardProgramPayload,
  ReferralAttribution,
  ReferralCode,
  ReferralCodeQuery,
  ReferralRewardLedgerEntry,
  ReferralRewardProgram,
  RewardLedgerStatus,
  UpdateReferralCodePayload,
  UpdateReferralRewardProgramPayload,
} from '../types';

/**
 * osprey-api `/referrals/*` (PE-2190 FE, backend PE-2189) — codes, reward programs and the
 * reward ledger. TCS-staff only: REFERRAL.READ at class level, MANAGE on every write. Not
 * merchant- or tenant-scoped, so `/referrals` is absent from ApiFetch's scoped prefixes.
 */
export const referralsApi = {
  listCodes: async (query: ReferralCodeQuery = {}): Promise<ReferralCode[]> => {
    const search = new URLSearchParams();
    if (query.referrerType) search.append('referrerType', query.referrerType);
    if (query.isActive !== undefined) search.append('isActive', String(query.isActive));
    const qs = search.toString();
    const response = await ApiFetch.get<Enveloped<ReferralCode[]>>(
      `/referrals/codes${qs ? `?${qs}` : ''}`,
    );
    return unwrap(response);
  },

  /** 409 when the code is already taken. */
  createCode: async (payload: CreateReferralCodePayload): Promise<ReferralCode> => {
    const response = await ApiFetch.post<Enveloped<ReferralCode>>('/referrals/codes', payload);
    return unwrap(response);
  },

  /** PATCH — `ApiFetch.put` issues PATCH. */
  updateCode: async (id: string, payload: UpdateReferralCodePayload): Promise<ReferralCode> => {
    const response = await ApiFetch.put<Enveloped<ReferralCode>>(`/referrals/codes/${id}`, payload);
    return unwrap(response);
  },

  /**
   * Links a contact to the referrer who sent them. 400 for an inactive code or a self-referral
   * (contact email = referrer email); 409 once the contact is already attributed.
   */
  attribute: async (code: string, contactId: string): Promise<ReferralAttribution> => {
    const response = await ApiFetch.post<Enveloped<ReferralAttribution>>(
      `/referrals/codes/${encodeURIComponent(code)}/attribute`,
      { contactId },
    );
    return unwrap(response);
  },

  listPrograms: async (): Promise<ReferralRewardProgram[]> => {
    const response = await ApiFetch.get<Enveloped<ReferralRewardProgram[]>>(
      '/referrals/reward-programs',
    );
    return unwrap(response);
  },

  createProgram: async (
    payload: CreateReferralRewardProgramPayload,
  ): Promise<ReferralRewardProgram> => {
    const response = await ApiFetch.post<Enveloped<ReferralRewardProgram>>(
      '/referrals/reward-programs',
      payload,
    );
    return unwrap(response);
  },

  updateProgram: async (
    id: string,
    payload: UpdateReferralRewardProgramPayload,
  ): Promise<ReferralRewardProgram> => {
    const response = await ApiFetch.put<Enveloped<ReferralRewardProgram>>(
      `/referrals/reward-programs/${id}`,
      payload,
    );
    return unwrap(response);
  },

  /**
   * Newest trigger first. Server filters on status only — the entry carries `attributionId`,
   * not the referrer, and there is no attribution list route, so it cannot be narrowed to a
   * referrer from here.
   */
  listRewards: async (status?: RewardLedgerStatus): Promise<ReferralRewardLedgerEntry[]> => {
    const response = await ApiFetch.get<Enveloped<ReferralRewardLedgerEntry[]>>(
      `/referrals/rewards${status ? `?status=${status}` : ''}`,
    );
    return unwrap(response);
  },

  /** PENDING → APPROVED → PAID, one step at a time. Records the action; moves no money. */
  updateRewardStatus: async (
    id: string,
    status: RewardLedgerStatus,
  ): Promise<ReferralRewardLedgerEntry> => {
    const response = await ApiFetch.put<Enveloped<ReferralRewardLedgerEntry>>(
      `/referrals/rewards/${id}/status`,
      { status },
    );
    return unwrap(response);
  },
};
